import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { authFetch } from "@/api/auth-fetch";
import type { Notification } from "@/api/models";
import { useAuthStore } from "@/stores/auth";

/**
 * Pinia store for in-app notifications.
 *
 * The badge in the topbar drives state from `unreadCount`, which is
 * polled every 60s via `startPolling()` (cheap GET that returns just
 * a number — see `/api/v1/notifications/unread-count`). Polling stops
 * automatically when the user logs out.
 *
 * The full list is loaded lazily — when the dropdown opens or the
 * user navigates to /notifications. This keeps the steady-state
 * traffic at a single ~1KB JSON every minute.
 */
export const useNotificationsStore = defineStore("notifications", () => {
  const list = ref<Notification[]>([]);
  const unreadCount = ref(0);
  const loading = ref(false);
  const error = ref<string | null>(null);
  let pollHandle: ReturnType<typeof setInterval> | null = null;

  const visible = computed(() =>
    list.value.filter((n) => n.dismissedAt === null),
  );
  const unreadList = computed(() =>
    visible.value.filter((n) => n.readAt === null),
  );

  async function fetchList(status: "unread" | "all" = "all"): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const url =
        status === "unread"
          ? "/api/v1/notifications?unreadOnly=true"
          : "/api/v1/notifications";
      const res = await authFetch(url);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const body = (await res.json()) as { data: Notification[] };
      list.value = body.data ?? [];
    } catch (e) {
      error.value = (e as Error).message;
      list.value = [];
    } finally {
      loading.value = false;
    }
  }

  async function fetchUnreadCount(): Promise<void> {
    try {
      const res = await authFetch("/api/v1/notifications/unread-count");
      if (!res.ok) return;
      const body = (await res.json()) as { data: { count: number } };
      unreadCount.value = body.data?.count ?? 0;
    } catch {
      // Network blips during polling shouldn't be loud.
    }
  }

  async function markRead(id: string): Promise<void> {
    const res = await authFetch(`/api/v1/notifications/${id}`, {
      method: "PATCH",
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const body = (await res.json()) as { data: Notification };
    const idx = list.value.findIndex((n) => n.id === id);
    if (idx >= 0) list.value[idx] = body.data;
    if (unreadCount.value > 0) unreadCount.value -= 1;
  }

  async function dismiss(id: string): Promise<void> {
    const res = await authFetch(`/api/v1/notifications/${id}`, {
      method: "DELETE",
    });
    if (!res.ok && res.status !== 204) {
      throw new Error(`HTTP ${res.status}`);
    }
    const wasUnread = list.value.find((n) => n.id === id)?.readAt === null;
    list.value = list.value.filter((n) => n.id !== id);
    if (wasUnread && unreadCount.value > 0) unreadCount.value -= 1;
  }

  /**
   * Starts the 60s unread-count poller. Idempotent — calling it
   * twice doesn't double-poll. Stops automatically once auth is lost
   * (the next tick after logout sees `isAuthenticated === false` and
   * skips the request, but we also nuke the handle on logout in App.vue).
   */
  function startPolling(): void {
    if (pollHandle) return;
    const auth = useAuthStore();
    void fetchUnreadCount();
    pollHandle = setInterval(() => {
      if (!auth.isAuthenticated) {
        stopPolling();
        return;
      }
      void fetchUnreadCount();
    }, 60_000);
  }

  function stopPolling(): void {
    if (pollHandle) {
      clearInterval(pollHandle);
      pollHandle = null;
    }
    unreadCount.value = 0;
    list.value = [];
  }

  return {
    list,
    unreadCount,
    loading,
    error,
    visible,
    unreadList,
    fetchList,
    fetchUnreadCount,
    markRead,
    dismiss,
    startPolling,
    stopPolling,
  };
});
